import type { VideoSeasonType } from "~/components/blocks/product/type-guard";
import { generateEpisodeNumberTitle } from "~/util/generate-episode-number-title";

type Return = {
  episodeSubText: string;
  episodeName?: string;
} | null;

type Args = {
  continueWatching: VideoSeasonType["continueWatching"];
  isStreamable: boolean;
  ppvProducts: VideoSeasonType["episodes"]["edges"][number]["node"]["ppvProducts"];
  svodProduct: VideoSeasonType["episodes"]["edges"][number]["node"]["svodProduct"];
  episodeContentId: string;
  isSingleEpisode: boolean;
  isEpisodeDetail: boolean;
  episodeTitle: string;
  episodeNumber: string;
  episodeNumberName: VideoSeasonType["episodes"]["edges"][number]["node"]["episodeNumberName"];
  playInfo?: NonNullable<VideoSeasonType["episode"]>["playInfo"];
};

export const getPlayButtonText = (args: Args): Return => {
  const {
    continueWatching,
    isStreamable,
    ppvProducts,
    svodProduct,
    episodeContentId,
    isSingleEpisode,
    isEpisodeDetail,
    episodeTitle,
    episodeNumberName,
    playInfo,
  } = args;

  if (isEpisodeDetail) {
    const episodeDetailTitle = generateEpisodeNumberTitle({ episodeTitle, episodeNumberName }).trim();

    if (isStreamable) {
      const resumePart = playInfo?.parts.find(({ number }) => number === playInfo.resumePartNumber);
      const hasResume = (resumePart?.resumePoint ?? 0) > 0;
      const continueText = hasResume ? "続きから" : "";

      return isSingleEpisode
        ? { episodeSubText: `${continueText}再生` }
        : { episodeSubText: `を${continueText}再生`, episodeName: episodeDetailTitle };
    }
    if (ppvProducts.length > 0) {
      return isSingleEpisode
        ? { episodeSubText: "購入" }
        : { episodeSubText: "を購入", episodeName: episodeDetailTitle };
    }
    if (svodProduct) {
      return isSingleEpisode
        ? { episodeSubText: "再生" }
        : { episodeSubText: "を再生", episodeName: episodeDetailTitle };
    }
    return null;
  }

  // レジューム情報がある場合
  if (continueWatching !== undefined && continueWatching !== null) {
    const {
      resumePoint,
      content: {
        episodeTitle: continueEpisodeTitle,
        episodeNumberName: continueEpisodeNumberName,
        viewingRights: { isStreamable: isContinueStreamable },
        ppvProducts,
        svodProduct,
      },
    } = continueWatching;

    const multiEpisodeName = generateEpisodeNumberTitle({ episodeTitle, episodeNumberName }).trim();

    /** エピソード話数表記+エピソードタイトル(特定文字数以上で省略) */
    const multiContinueEpisodeName = generateEpisodeNumberTitle({
      episodeTitle: continueEpisodeTitle,
      episodeNumberName: continueEpisodeNumberName,
    }).trim();

    // episodeContentId === contentId の場合の処理は以下。続きから見るの視聴権利がある場合
    if (isContinueStreamable) {
      if (resumePoint > 0) {
        return isSingleEpisode
          ? { episodeSubText: "続きから再生" }
          : { episodeSubText: "を続きから再生", episodeName: multiContinueEpisodeName };
      }
      return isSingleEpisode
        ? { episodeSubText: "再生" }
        : { episodeSubText: "を再生", episodeName: multiContinueEpisodeName };
    }
    // 続きから見るの視聴権利がない場合
    // PPV商品の場合
    if (ppvProducts.length > 0) {
      return isSingleEpisode ? { episodeSubText: "購入" } : { episodeSubText: "を購入", episodeName: multiEpisodeName };
    }
    // SVOD作品の場合
    if (svodProduct !== null && svodProduct !== undefined) {
      const {
        content: { episodeTitle: continueEpisodeTitle, episodeNumberName: continueEpisodeNumberName },
      } = continueWatching;
      const episodeName = generateEpisodeNumberTitle({
        episodeTitle: continueEpisodeTitle,
        episodeNumberName: continueEpisodeNumberName,
      });

      return isSingleEpisode ? { episodeSubText: "再生" } : { episodeSubText: "を再生", episodeName };
    }
    return null;
  }

  /** エピソード話数表記+エピソードタイトル(特定文字数以上で省略) */
  const multiEpisodeName = generateEpisodeNumberTitle({ episodeTitle, episodeNumberName });

  // レジューム情報がない場合
  // videoSeason.episodes(type:MAIN)[0] が空配列の場合、何も表示しない
  if (episodeContentId === "") return null;

  // フォーカスされたエピソードの視聴権利がある場合
  if (isStreamable) {
    return isSingleEpisode ? { episodeSubText: "再生" } : { episodeSubText: "を再生", episodeName: multiEpisodeName };
  }
  // フォーカスされたエピソードの視聴権利がない場合
  // PPV商品の場合
  if (ppvProducts.length > 0) {
    return isSingleEpisode ? { episodeSubText: "購入" } : { episodeSubText: "を購入", episodeName: multiEpisodeName };
  }
  // SVOD作品の場合
  if (svodProduct !== null && svodProduct !== undefined) {
    return isSingleEpisode ? { episodeSubText: "再生" } : { episodeSubText: "を再生", episodeName: multiEpisodeName };
  }
  return null;
};
